import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from "react";
import * as d3 from "d3";
import "./chart.css";
import { MdLabel, MdLabelOff } from "react-icons/md";

const StackedAreaChart = forwardRef(
  (
    {
      xData,
      yData,
      width = Math.max(xData.length * 30, 500),
      height = Math.max(yData.length, 500),
      drag = false,
      edit = false,
      forecastId
    },
    ref
  ) => {
    const svgRef = useRef();
    const tooltipRef = useRef();
    const zoomTransformRef = useRef(d3.zoomIdentity);
    const xScaleRef = useRef();
    const yScaleRef = useRef();
    const [action, setAction] = useState("switch");
    const [contextMenu, setContextMenu] = useState(null);
    const [leftClickMenu, setLeftClickMenu] = useState(null);
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [selectedParameter, setSelectedParameter] = useState(null);
    const [chartData, setChartData] = useState(yData);
    const [chartWidth, setChartWidth] = useState(window.innerWidth * 0.96);
    const menuRef = useRef(null);
    const [showLegend, setShowLegend] = useState(false);
    const [legendSearch, setLegendSearch] = useState("");
    const baseYDataRef = useRef([]);
    const dragContextRef = useRef({});
    const [hiddenSeriesMap, setHiddenSeriesMap] = useState({});
    const initialChartHeight = 600;
    const [chartHeight, setChartHeight] = useState(window.innerHeight * 0.70);
    const [pivotState, setPivotState] = useState({});
    const [switchState, setSwitchState] = useState({});
    const [lineState, setLineState] = useState({});
    const [forceYMinZero, setForceYMinZero] = useState(false);
    const [yMin, setYMin] = useState(0);
    const [yMax, setYMax] = useState(0);
    useImperativeHandle(ref, () => ({
      getUpdatedChartData: () => {
        if (!baseYDataRef.current) return [];
        return baseYDataRef.current.map(series => {
          return {
            ...series,
            values: series.values.map((base, i) => {
              const key = `${series.field}_${i}`;
              const pivot = pivotState[key] || 0;
              const sw = switchState[key] || 0;
              const l = lineState[key] || 0;
              return base + pivot + sw + l;
            }),
          };
        });
      },
    }));

    useEffect(() => {
      setChartHeight(initialChartHeight);
    }, [yData]);

    // useEffect(() => {
    //   const initialMax = Math.ceil(d3.max(yData.flatMap(d => d.values)) * 1.05);
    //   setYMax(prev => Math.max(prev, initialMax));
    // }, [yData]);

    useEffect(() => {
      if (forecastId) {
        const stored = localStorage.getItem(`hiddenSeries_${forecastId}`);
        if (stored) {
          try {
            setHiddenSeriesMap(JSON.parse(stored));
          } catch (e) {
            // console.error("Invalid JSON in localStorage:", stored);
          }
        } else {
          setHiddenSeriesMap({});
        }
      }
    }, [forecastId]);

    useEffect(() => {
      if (forecastId) {
        localStorage.setItem(`hiddenSeries_${forecastId}`, JSON.stringify(hiddenSeriesMap));
      }
    }, [hiddenSeriesMap, forecastId]);

    useEffect(() => {
      if (!xData || !yData) return;
      setPivotState({});
      setSwitchState({});
      setLineState({});
      baseYDataRef.current = [];
    }, [xData, yData]);

    useEffect(() => {
      const updateChartSize = () => setChartWidth(window.innerWidth * 0.90);
      window.addEventListener("resize", updateChartSize);
      return () => window.removeEventListener("resize", updateChartSize);
    }, []);

    useEffect(() => {
      const updateChartSize = () => {
        setChartWidth(window.innerWidth * 0.90);
        setChartHeight(window.innerHeight * 0.70);
      };
      window.addEventListener("resize", updateChartSize);
      return () => window.removeEventListener("resize", updateChartSize);
    }, []);

    useEffect(() => {
      if (!yData || yData.length === 0) return;

      const allValues = yData.flatMap(d =>
        d.values.filter(v => typeof v === "number" && !isNaN(v))
      );

      if (allValues.length === 0) return;

      const min = Math.min(...allValues);
      const max = Math.max(...allValues);
      const paddedMax = Math.ceil(max * 1.05);

      setYMin(min);
      setYMax(paddedMax);
    }, [yData]);

    useEffect(() => {
      if (!xData || !yData) return;
      const margin = { top: 30, right: 20, bottom: 50, left: 70 };
      const width = chartWidth;
      const height = chartHeight;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();
      svg
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .style("background-color", "#F2F2F2");

      const maxLabels = Math.floor(width / 80);
      const interval = Math.ceil(xData.length / maxLabels);

      const chartGroup = svg.append("g")
        .attr("class", "chart-content")
        .attr("clip-path", "url(#clip)");

      const xScale = d3.scaleLinear()
        .domain([0, xData.length - 1])
        .range([margin.left, width - margin.right]);

      const yBuffer = 1000;
      const dynamicYMin = forceYMinZero ? 0 : Math.max(0, yMin - yBuffer);
      const dynamicYMax = yMax;
      const yScale = d3.scaleLinear()
        .domain([dynamicYMin, dynamicYMax])
        .range([height - margin.bottom, margin.top]);

      xScaleRef.current = xScale;
      yScaleRef.current = yScale;

      const xAxisGroup = svg.append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(0, ${height - margin.bottom})`);

      const yAxisGroup = svg.append("g")
        .attr("class", "y-axis")
        .attr("transform", `translate(${margin.left}, 0)`);

      const xAxis = d3.axisBottom(xScale)
        .tickFormat(i => xData[Math.round(i)])
        .ticks(xData.length / interval);

      const yAxis = d3.axisLeft(yScale);

      xAxisGroup.call(xAxis)
        .selectAll("text")
        .attr("transform", "rotate(-45)")
        .style("text-anchor", "end")
        .style("font-size", "14px")
        .style("font-weight", "bold");

      yAxisGroup.call(yAxis);
      yAxisGroup.selectAll("text")
        .style("font-size", "14px")
        .style("font-weight", "bold");

      const tooltip = d3.select(tooltipRef.current)
        .style("opacity", 0)
        .style("position", "absolute")
        .style("pointer-events", "none")
        .style("background-color", "rgba(0,0,0,0.6)")
        .style("color", "white")
        .style("padding", "5px")
        .style("border-radius", "4px");

      const grid = svg.append("g").attr("class", "grid");

      grid.selectAll("line.horizontal")
        .data(yScale.ticks())
        .enter()
        .append("line")
        .attr("x1", margin.left)
        .attr("x2", width - margin.right)
        .attr("y1", d => yScale(d))
        .attr("y2", d => yScale(d))
        .style("stroke", "#ccc")
        .style("stroke-width", 1)
        .style("shape-rendering", "crispEdges");

      grid.selectAll(".grid-line-vertical")
        .data(xScale.ticks(xData.length / interval))
        .enter()
        .append("line")
        .attr("class", "grid-line-vertical")
        .attr("x1", d => xScale(d))
        .attr("x2", d => xScale(d))
        .attr("y1", margin.top)
        .attr("y2", height - margin.bottom)
        .style("stroke", "#ccc")
        .style("stroke-width", 1)
        .style("stroke-dasharray", "4,4");

      svg.on("contextmenu", (event) => {
        if (!drag) return;
        event.preventDefault();
        const [mouseX, mouseY] = d3.pointer(event);
        const menuWidth = 160;
        const menuHeight = 100;
        const padding = 10;
        const clampedX = Math.min(mouseX, chartWidth - menuWidth - padding);
        const clampedY = Math.min(mouseY, chartHeight - menuHeight - padding);
        setContextMenu({ x: clampedX, y: clampedY });
        setLeftClickMenu(null);
      });

      d3.select(window).on("click", () => setContextMenu(null));

      if (baseYDataRef.current.length === 0) {
        baseYDataRef.current = JSON.parse(JSON.stringify(yData));
      }

      svg.append("defs")
        .append("clipPath")
        .attr("id", "clip")
        .append("rect")
        .attr("x", margin.left)
        .attr("y", 0)
        .attr("width", width - margin.left - margin.right)
        .attr("height", height);

      // Draw lines
      yData.forEach(series => {
        if (hiddenSeriesMap[series.field]) return;

        const allSeriesValues = yData.map(s => s.values.map((v, i) => v || 0));
        const flattenedValues = allSeriesValues.flat();
        const maxValue = Math.max(...flattenedValues);
        const minValue = Math.min(...flattenedValues);
        const seriesMax = Math.max(...series.values);
        const seriesMin = Math.min(...series.values);

        const line = d3.line()
          .defined(d => d !== 0)
          .x((_, i) => xScaleRef.current(i))
          .y((_, i) => {
            const key = `${series.field}_${i}`;
            const base = baseYDataRef.current.find(s => s.field === series.field)?.values[i] || 0;
            const pivot = pivotState[key] || 0;
            const sw = switchState[key] || 0;
            const l = lineState[key] || 0;
            return yScale(base + pivot + sw + l);
          });

        chartGroup.append("path")
          .datum(series.values)
          .attr("fill", "none")
          .attr("stroke", series.color || "#000")
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", (seriesMax === maxValue || seriesMin === minValue) ? "0" : "4,2")
          .attr("opacity", (seriesMax === maxValue || seriesMin === minValue) ? 0 : 1)
          .attr("class", `line-${series.field.replace(/\s+/g, "-")}`)
          .attr("d", line);
      });

      chartGroup.selectAll(".hover-rect").remove();
      chartGroup.selectAll(".hover-rect")
        .data(xData)
        .enter()
        .append("rect")
        .attr("class", "hover-rect")
        .attr("x", (_, i) => xScale(i) - (width / xData.length) / 2)
        .attr("y", margin.top)
        .attr("width", width / xData.length)
        .attr("height", height - margin.top - margin.bottom)
        .attr("fill", "transparent")
        .on("mouseover", (event, d) => {
          const index = xData.indexOf(d);
          let tooltipHTML = `<div style="color:white;font-size:14px;font-weight:bold;">${xData[index]}</div>`;

          baseYDataRef.current.forEach(series => {
            if (hiddenSeriesMap[series.field]) return;

            const key = `${series.field}_${index}`;
            const base = series.values[index] || 0;
            const pivot = pivotState[key] || 0;
            const sw = switchState[key] || 0;
            const l = lineState[key] || 0;
            const finalY = base + pivot + sw + l;

            tooltipHTML += `
          <div style="display:flex;align-items:center;margin-top:2px;">
            <div style="width:10px;height:10px;background-color:${series.color};margin-right:5px;border:1px solid white;"></div>
            <span style="font-size:13px;font-weight:bold;">${series.field}:</span>
            <span style="margin-left:5px;font-size:13px;">${finalY.toLocaleString()}</span>
          </div>`;
          });

          const [mouseX, mouseY] = d3.pointer(event, svg.node());
          let tooltipX = mouseX + 10;
          let tooltipY = mouseY - 10;
          const tooltipWidth = 200;
          const tooltipHeight = 100;
          const padding = 20;

          if (tooltipX + tooltipWidth > chartWidth) tooltipX = chartWidth - tooltipWidth - padding;
          if (tooltipY < 0) tooltipY = padding;

          tooltip
            .style("opacity", 1)
            .style("left", `${tooltipX}px`)
            .style("top", `${tooltipY}px`)
            .html(tooltipHTML);
        })
        .on("mouseout", () => tooltip.style("opacity", 0));

      for (let i = 0; i < xData.length; i++) {
        let valuesAtIndex = [];
        yData.forEach(series => {
          if (hiddenSeriesMap[series.field]) return;

          const key = `${series.field}_${i}`;
          const base = baseYDataRef.current.find(s => s.field === series.field)?.values[i] || 0;
          const pivot = pivotState[key] || 0;
          const sw = switchState[key] || 0;
          const l = lineState[key] || 0;
          const value = base + pivot + sw + l;

          if (!isNaN(value)) valuesAtIndex.push(value);
        });

        if (!valuesAtIndex.length) continue;

        const minVal = Math.min(...valuesAtIndex);
        const maxVal = Math.max(...valuesAtIndex);

        chartGroup.selectAll(`.x-minmax-bar-${i}`).remove();
        chartGroup.append("rect")
          .attr("class", `x-minmax-bar-${i}`)
          .attr("x", xScale(i) - 4)
          .attr("y", yScale(maxVal))
          .attr("width", 8)
          .attr("height", yScale(minVal) - yScale(maxVal))
          .attr("fill", "lightblue")
          .attr("opacity", 0.6);
      }

    }, [
      xData, yData, chartWidth, chartHeight, yMax, action,
      startTime, endTime, selectedParameter,
      pivotState, switchState, lineState, hiddenSeriesMap
    ]);


    return (
      <div className="chart-container">
        <svg ref={svgRef}></svg>
        <div ref={tooltipRef}></div>

        {/* Toggle Button */}
        <button
          onClick={() => setShowLegend(!showLegend)}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 10,
            padding: "6px 12px",
            backgroundColor: "transparent",
            color: "black",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          {showLegend ? <MdLabelOff size={24} /> : <MdLabel size={24} />}
        </button>

        {/* Legend Panel */}
        {showLegend && (
          <div
            style={{
              position: "absolute",
              top: 50,
              right: 10,
              width: "220px",
              maxHeight: "60%",
              overflowY: "auto",
              backgroundColor: "#fff",
              boxShadow: "0 0 10px rgba(0,0,0,0.2)",
              borderRadius: "6px",
              padding: "10px",
              zIndex: 9
            }}
          >
            <input
              type="text"
              value={legendSearch}
              onChange={(e) => setLegendSearch(e.target.value)}
              placeholder="Search series..."
              style={{
                width: "100%",
                padding: "6px",
                marginBottom: "10px",
                border: "1px solid #ccc",
                borderRadius: "4px"
              }}
            />
            {yData
              .filter((series) =>
                series.field.toLowerCase().includes(legendSearch.toLowerCase())
              )
              .map((series, i) => {
                const lineClass = `.line-${series.field.replace(/\s+/g, "-")}`;
                const pointClass = `.point-${series.field.replace(/\s+/g, "-")}`;
                const safeClass = `legend-${series.field.replace(/\s+/g, "-")}`;
                const isHidden = hiddenSeriesMap[series.field] || false;

                return (
                  <div
                    key={i}
                    className={safeClass}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      marginBottom: "6px"
                    }}
                    onClick={() => {
                      const line = d3.select(lineClass);
                      const points = d3.selectAll(pointClass);
                      const display = isHidden ? "block" : "none";
                      line.style("display", display);
                      points.style("display", display);
                      setHiddenSeriesMap(prev => ({
                        ...prev,
                        [series.field]: !prev[series.field]
                      }));
                    }}
                  >
                    <div
                      style={{
                        width: "14px",
                        height: "14px",
                        backgroundColor: series.color || "#000",
                        marginRight: "6px",
                        border: "1px solid #000"
                      }}
                    ></div>
                    <span
                      style={{
                        textDecoration: isHidden ? "line-through" : "none",
                        color: "#333",
                        fontSize: "13px"
                      }}
                    >
                      {series.field}
                    </span>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  });

export default StackedAreaChart;